import React from "react";
import { Menu, useResourceDefinitions, useSidebarState, useNotify } from "react-admin";
import { MenuItem, ListItemIcon, ListItemText, Collapse, List, Typography, Box, CircularProgress } from "@mui/material";
import { NavGlyph } from "./navGlyphs";
import authInstance from "../services/httpClient";
import { useNavigate, useLocation, useHref } from "react-router-dom";
import { usePermissions, canViewResource } from "../context/PermissionContext";

/**
 * Rail metrics, from the reskin prototype (deeliver-dash-skin): a 15px/600 row
 * with a 17px stroke glyph, 9px 16px padding; mono uppercase group headers on a
 * hairline. Colours are the ground's CSS variables (theme/grounds.ts) so the
 * rail re-themes with the picker without a hook.
 */
const NAV_ROW_FONT_PX = 15;
const NAV_ROW_LINE_PX = 24;
const NAV_GLYPH_PX = 17;
const NAV_GLYPH_GAP_PX = 11;

/**
 * Which sidebar section owns which route, so the nav can follow navigation that did
 * not come from the nav itself. Matched longest-prefix-first (see the effect below).
 */
const ROUTE_SECTIONS: Array<[string, string]> = [
  ['/live-orders', 'orders'],
  ['/orders', 'orders'],
  ['/uber-deliveries', 'orders'],
  ['/tfs-deliveries', 'orders'],
  ['/content/control', 'config'],
  ['/stores/command-center', 'config'],
  ['/products', 'products'],
  ['/deeliver', 'products'],
  ['/vaperite', 'vaperite'],
  // Company-wide pages, not Vaperite's (moved out of the Vaperite section 2026-10-01).
  ['/webhook-endpoints', 'config'],
  ['/admin/staff', 'adminTools'],
  ['/marketing', 'marketing'],
  ['/messages', 'marketing'],
  // Everything Uber Eats has its own section (Jason, 2026-09-16).
  ['/uber-orders', 'uberEats'],
  ['/uber-menu', 'uberEats'],
  ['/uber-promotions', 'uberEats'],
  ['/uber-tools', 'uberEats'],
  // The Discount Engine pages live under Marketing (Jason, 26 Oct 2026)
  ['/discounts', 'marketing'],
  ['/customers', 'customers'],
  // '/tools/vetting' is listed above and wins on longest prefix — it belongs to
  // Products, not here.
  ['/tools', 'adminTools'],
  ['/missions', 'adminTools'],
  ['/customer-map', 'adminTools'],
  ['/admin/metrics', 'adminTools'],
  ['/settings/system-core', 'adminTools'],
  ['/webhook-dispatches', 'adminTools'],
  ['/admin-functions', 'adminTools'],
  ['/integrations/webhook-testing', 'adminTools'],
];

/**
 * CustomMenu Component
 *
 * Role-based navigation menu with collapsible sections.
 * Menu items are filtered based on user role (owner, admin, marketing, manager, staff).
 *
 * The row set and group order are the reskin prototype's (deeliver-dash-skin),
 * verbatim — Jason, 2026-09-14: "match the prototype menu as it is now, nothing
 * hidden", so the keep-or-delete pass with William can be had against the rail
 * everyone is looking at. One later deviation: Public Holiday Manager moved to
 * the Store Command Center's header (Jason, later that day). Rows the
 * prototype hides are gone from here too:
 * Marketing & SEO (not in use) and the SKC-era Webhooks row (/skc/webhooks has
 * no <Route>, it rendered a not-found page). Their pages and routes are
 * untouched.
 *
 * All sections start collapsed, expand on click, one at a time:
 * - Orders & Operations
 * - Uber Eats
 * - Customers
 * - Products
 * - Configuration
 * - Vaperite
 * - Marketing
 * - Admin Tools
 *
 * The rail does not collapse to icons. The prototype has no burger (removed
 * 2026-09-12), so react-admin's sidebar is pinned open here and the app bar
 * hides its toggle; see CustomAppBar.
 */

/** Collapsible section header */
function SectionToggle({
  title,
  isOpen,
  onToggle,
}: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <MenuItem
      onClick={onToggle}
      sx={{
        px: 2,
        pt: '13px',
        pb: '11px',
        mt: '2px',
        minHeight: 'unset',
        borderRadius: 0,
        borderTop: '1px solid var(--line-soft)',
        color: isOpen ? 'var(--tx2)' : 'var(--tx3)',
        '&:hover, &.Mui-focusVisible': { bgcolor: 'transparent', color: 'var(--ember)' },
      }}
    >
      <Typography
        component="span"
        sx={{
          flex: 1,
          fontFamily: 'var(--f-data)',
          fontSize: '11.5px',
          fontWeight: 700,
          lineHeight: 1.6,
          color: 'inherit',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
        }}
      >
        {title}
      </Typography>
      {/* The prototype's chevron is a text triangle, not an icon: 8.5px, ember
          when the group is open, and it turns rather than swaps so the
          accordion reads as one thing moving. */}
      <Box
        component="span"
        aria-hidden
        sx={{
          fontFamily: 'var(--f-data)',
          fontSize: '8.5px',
          lineHeight: 1.6,
          color: isOpen ? 'var(--ember)' : 'inherit',
          opacity: isOpen ? 1 : 0.7,
          transform: isOpen ? 'none' : 'rotate(180deg)',
          transition: 'transform .18s cubic-bezier(.2,.7,.2,1)',
        }}
      >
        &#9650;
      </Box>
    </MenuItem>
  );
}

/**
 * One rail row: glyph + label. With `to` it is a real link (an <a href>), so middle-click,
 * Ctrl/Cmd-click and "Open in new tab" work like any link (Jason, 25 Oct 2026); a plain
 * left click still navigates in-app. `onClick` runs first (e.g. collapse a section). A row
 * without `to` is an action, not a page (Public Holiday Mode).
 */
export function NavItem({
  icon,
  label,
  to,
  onClick,
  selected,
}: {
  icon: React.ReactElement;
  label: string;
  to?: string;
  onClick?: () => void;
  selected: boolean;
}) {
  const navigate = useNavigate();
  const href = useHref(to ?? '');
  const handleClick = (e: React.MouseEvent) => {
    // Modified or non-primary clicks open a new tab/window: leave those to the browser.
    if (to && (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return;
    if (to) e.preventDefault();
    onClick?.();
    if (to) navigate(to);
  };
  return (
    <MenuItem
      {...(to ? { component: 'a' as const, href } : {})}
      onClick={handleClick}
      selected={selected}
      sx={{
        justifyContent: 'flex-start',
        px: 2,
        py: '9px',
        minHeight: NAV_ROW_LINE_PX + 18,
        borderRadius: 0,
        position: 'relative',
        color: 'var(--tx)',
        // Both nested from the row on purpose. MenuItem's own styles put a 36px
        // minWidth on the icon slot, and the theme's MuiListItemText override sets
        // 0.875rem on the label; a plain sx on either child loses to them, a
        // longer selector from here wins.
        '& .MuiListItemIcon-root': {
          color: 'inherit',
          minWidth: NAV_GLYPH_PX + NAV_GLYPH_GAP_PX,
        },
        '& .MuiListItemText-root': { my: 0 },
        '& .MuiListItemText-root .MuiListItemText-primary': {
          fontSize: NAV_ROW_FONT_PX,
          fontWeight: 600,
          lineHeight: `${NAV_ROW_LINE_PX}px`,
        },
        '&:hover, &.Mui-focusVisible': { bgcolor: 'var(--hover)', color: 'var(--ember)' },
        // The active page reads in the ground's ok tone on its own tint, with a
        // 2px bar inset from the row's top and bottom — the prototype's .nav-item.on.
        '&.Mui-selected': {
          bgcolor: 'var(--ok-bg)',
          color: 'var(--ok)',
          // Theme-level MuiListItemIcon paints selected icons ember; the rail is the one place that is wrong.
          '& .MuiListItemIcon-root': { color: 'var(--ok)' },
          '&:hover': { bgcolor: 'var(--ok-bg)', color: 'var(--ok)' },
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 6,
            bottom: 6,
            width: 2,
            bgcolor: 'var(--ok)',
            borderRadius: '0 2px 2px 0',
          },
        },
      }}
    >
      <ListItemIcon sx={{ '& svg': { width: NAV_GLYPH_PX, height: NAV_GLYPH_PX, display: 'block' } }}>
        {icon}
      </ListItemIcon>
      <ListItemText primary={label} />
    </MenuItem>
  );
}

/** Today's date in Johannesburg as YYYY-MM-DD — the override API's shape. */
const todayInSA = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Johannesburg' }).format(new Date());

const CustomMenu: React.FC = () => {
  const resources = useResourceDefinitions();
  const [open, setOpen] = useSidebarState();
  const location = useLocation();
  const notify = useNotify();
  const { role, loading, hasAnyRole, canSeePath, canSeeSection } = usePermissions();
  const [applyingHoliday, setApplyingHoliday] = React.useState(false);

  /**
   * The rail never collapses to icons: the prototype has no burger, so an
   * icon-only state has no design behind it. react-admin remembers a closed
   * sidebar across sessions, which is why this is an effect and not a default.
   */
  React.useEffect(() => {
    if (!open) setOpen(true);
  }, [open, setOpen]);

  /**
   * Public Holiday Mode: treat TODAY as a public holiday, across every store.
   *
   * The old button POSTed /admin/public-holidays/apply-today, which forced a
   * 6pm liquor cutoff and recorded no reason; that endpoint is gone. An
   * override for today's date is what replaced it — it applies immediately
   * and says why — so that is what this row does now. It is listed in the
   * Public Holiday Manager like any other override and can be removed there.
   */
  const applyPublicHolidayMode = async () => {
    const date = todayInSA();
    const confirmed = window.confirm(
      `Apply PUBLIC HOLIDAY MODE for today (${date})?\n\n` +
        "Every store trades by its holiday rule for the rest of the day — a store set to 'closed' stops taking orders.\n" +
        'Undo it in the Public Holiday Manager.'
    );
    if (!confirmed) return;
    const reason = window.prompt('Reason (shown in the Public Holiday Manager):', 'Public holiday mode') ?? '';

    setApplyingHoliday(true);
    try {
      await authInstance.post('/admin/public-holidays/overrides', { date, reason: reason.trim() || undefined });
      notify(`Public holiday mode applied for ${date}`, { type: 'success' });
    } catch (err: any) {
      notify(err?.response?.data?.message || 'Failed to apply public holiday mode', { type: 'error' });
    } finally {
      setApplyingHoliday(false);
    }
  };

  // Section collapse — accordion: only one section open at a time. Opening a
  // section auto-collapses whichever other section was open.
  const [openSection, setOpenSection] = React.useState<string | null>(null);
  const toggleSection = (key: string) =>
    setOpenSection((cur) => (cur === key ? null : key));

  /**
   * Open whichever section owns the current route.
   *
   * Navigating from outside the sidebar — the Dashboard’s “Jump to” tiles, a deep
   * link, the browser back button — used to leave the nav collapsed or sitting on a
   * different section, so the page you were on wasn’t visible in the menu and you had
   * to go hunting for where you were.
   *
   * Longest prefix wins: /tools/vetting belongs to PRODUCTS while /tools belongs to
   * ADMIN TOOLS, so a plain startsWith in declaration order would pick the wrong one.
   *
   * No owner means collapse. Dashboard is the only nav destination without one —
   * every other entry in the sidebar maps to a section — so going Home now closes
   * whatever accordion was left open instead of leaving it hanging over a page it
   * has nothing to do with. Reaching '/' any other way (the logo, a redirect after
   * login) collapses it too, which is why this lives in the route effect rather
   * than on the Dashboard item's onClick.
   */
  React.useEffect(() => {
    const owner = [...ROUTE_SECTIONS]
      .sort((a, b) => b[0].length - a[0].length)
      .find(([prefix]) => location.pathname === prefix || location.pathname.startsWith(prefix + '/'));
    setOpenSection(owner ? owner[1] : null);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  if (loading) {
    return null;
  }

  // Visibility. Owner/Admin: by role, narrowed by any ticks. Staff: by ticks alone
  // (canSeePath / canSeeSection), whatever the page (Jason, 31 Oct 2026).
  const isStaff = role === 'staff';
  const canView = (resource: string) => isStaff || canViewResource(resource, role);
  const isOwnerOrAdmin = role === 'owner' || role === 'admin';
  const roleAdmits = isOwnerOrAdmin || isStaff;
  // The Vaperite section is ours to look at, not the partner's: there is no
  // partner login. It used to also admit a 'skc' role, which was retired with the
  // SKC integration.
  const canAccessVaperite = roleAdmits;

  /** A group: header + its rows, mounted only while open. */
  const group = (key: string, title: string, rows: React.ReactNode, visible = canSeeSection(key)) => (visible ? [
    <SectionToggle key={`${key}-hdr`} title={title} isOpen={openSection === key} onToggle={() => toggleSection(key)} />,
    <Collapse key={`${key}-body`} in={openSection === key} timeout="auto" unmountOnExit>
      <List component="div" disablePadding>
        {rows}
      </List>
    </Collapse>,
  ] : null);

  return (
    <>
    {/* Every row is our own NavItem — react-admin's MenuItemLink is deliberately
        NOT used in this sidebar any more.

        It rendered four rows (Live Orders, Orders, Webhook Endpoints, Webhook
        Dispatches) with react-admin's own typography and icon metrics, so they
        never matched the rest of the menu. Styling it into line from the outside
        was tried and had to be reverted: a container-level override of
        .RaMenuItemLink-root / .MuiListItemIcon-root collided with its internal
        layout and stripped those rows' labels entirely, leaving bare icons.

        Owning the component removes the whole failure mode — nothing here depends
        on a framework internal class name. Keep it that way: if a new nav row is
        needed, add a NavItem. Resource gating (`resources[...]`) is unaffected;
        react-admin still registers the routes, we just navigate to them. */}
    <Menu>
      {/* ======== DASHBOARD (default landing) — always first ========
           Pinned to the top of the sidebar: the menu scrolls beneath it, but
           Dashboard stays reachable without scrolling back up. */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'var(--sunk)',
        }}
      >
        <NavItem
          icon={<NavGlyph name="dashboard" />}
          label="Dashboard"
          // Collapse here as well as in the route effect: clicking Dashboard
          // while already ON the dashboard doesn't change location.pathname, so
          // the effect never re-runs and the open accordion would just sit there.
          to="/"
          onClick={() => setOpenSection(null)}
          selected={isActive("/")}
        />
      </Box>

      {/* ======== ORDERS & OPERATIONS ======== */}
      {canView('orders') && group('orders', 'ORDERS & OPERATIONS', <>
        {canSeePath("/live-orders") && (
          <NavItem icon={<NavGlyph name="liveOrders" />} label="Live Orders" to="/live-orders" selected={isActive("/live-orders")} />
        )}
        {resources.orders && canSeePath("/orders") && (
            <NavItem icon={<NavGlyph name="orders" />} label="Orders" to="/orders" selected={isActive("/orders")} />
        )}
        {canSeePath("/uber-deliveries") && (
          <NavItem icon={<NavGlyph name="uberDeliveries" />} label="Uber Deliveries" to="/uber-deliveries" selected={isActive("/uber-deliveries")} />
        )}
        {canSeePath("/tfs-deliveries") && (
          <NavItem icon={<NavGlyph name="tfsDeliveries" />} label="TFS Deliveries" to="/tfs-deliveries" selected={isActive("/tfs-deliveries")} />
        )}
      </>)}

      {/* ======== UBER EATS ======== */}
      {/* Everything Uber Eats in one place: orders (from Orders & Operations),
          the live menu, promotions (from Marketing) and the menu tools (from
          Admin Tools). Uber Deliveries stays above — that is Uber Direct, the
          courier, not Uber Eats. */}
      {canView('orders') && group('uberEats', 'UBER EATS', <>
        {canSeePath("/uber-orders") && (
          <NavItem icon={<NavGlyph name="uberOrders" />} label="Uber Orders" to="/uber-orders" selected={isActive("/uber-orders")} />
        )}
        {roleAdmits && canSeePath("/uber-menu") && (
            <NavItem icon={<NavGlyph name="uberMenu" />} label="Uber Menu" to="/uber-menu" selected={isActive("/uber-menu")} />
        )}
        {roleAdmits && canSeePath("/uber-promotions") && (
            <NavItem icon={<NavGlyph name="uberPromotions" />} label="Uber Promotions" to="/uber-promotions" selected={isActive("/uber-promotions")} />
        )}
        {roleAdmits && canSeePath("/uber-tools") && (
            <NavItem icon={<NavGlyph name="uberTools" />} label="Uber Tools" to="/uber-tools" selected={isActive("/uber-tools")} />
        )}
      </>)}

      {/* ======== CUSTOMERS ======== */}
      {roleAdmits && group('customers', 'CUSTOMERS', <>
        {canSeePath("/customers") && (
          <NavItem icon={<NavGlyph name="customers" />} label="Customers" to="/customers" selected={isActive("/customers")} />
        )}
        {/* Placeholder page (Jason, 5 Nov 2026) */}
        {canSeePath("/customers/deeliver") && (
          <NavItem icon={<NavGlyph name="customers" />} label="Deeliver Customers" to="/customers/deeliver" selected={isActive("/customers/deeliver")} />
        )}
      </>)}

      {/* ======== DEELIVER ======== */}
      {/* Was PRODUCTS; renamed to match the VAPERITE section (Jason, 5 Nov 2026). The section key stays
          'products' so stored staff page grants are unchanged. */}
      {roleAdmits && group('products', 'DEELIVER', <>
        {/* Mirrors the Vaperite section: Item Staging, then Products (Jason, 23 Oct 2026). */}
        {canSeePath("/deeliver/staging") && (
          <NavItem icon={<NavGlyph name="vaperiteItemStaging" />} label="Deeliver Item Staging" to="/deeliver/staging" selected={isActive("/deeliver/staging")} />
        )}
        {canSeePath("/products") && (
          <NavItem icon={<NavGlyph name="productManagement" />} label="Deeliver Products" to="/products" selected={isActive("/products")} />
        )}
        {/* Deeliver's own Vend syncs, like Vaperite Sync (Jason, 31 Oct 2026) */}
        {canSeePath("/deeliver/sync") && (
          <NavItem icon={<NavGlyph name="syncOverview" />} label="Deeliver Sync" to="/deeliver/sync" selected={isActive("/deeliver/sync")} />
        )}
      </>)}

      {/* ======== CONFIGURATION ======== */}
      {roleAdmits && group('config', 'CONFIGURATION', <>
        {canView('banners') && canView('categories') && canSeePath("/content/control") && (
            <NavItem icon={<NavGlyph name="contentControl" />} label="Content Control" to="/content/control" selected={isActive("/content/control")} />
        )}
        {canView('command-center') && canSeePath("/stores/command-center") && (
            <NavItem icon={<NavGlyph name="storeCommandCenter" />} label="Store Command Center" to="/stores/command-center" selected={isActive("/stores/command-center")} />
        )}
        {resources["webhook-endpoints"] && canSeePath("/webhook-endpoints") && (
            <NavItem icon={<NavGlyph name="webhookEndpoints" />} label="Webhook Endpoints" to="/webhook-endpoints" selected={isActive("/webhook-endpoints")} />
        )}
      </>)}

      {/* ======== VAPERITE ======== */}
      {/* SKC is gone; Vaperite replaced it. The DATA FLOW changed, not just the
            label: their catalogue and stock are now POLLED on a timer and routed
            through the staging review queue. Nothing arrives by webhook — the
            inventory webhook is Deeliver's alone and refuses partner outlets.

            The SKC names this section used to carry — the /skc/* routes, the
            'skc' role and the canAccessSKC identifier — are gone as of
            2026-08-31. No redirect was left behind for /skc/staging: the only
            people who could reach it are us. */}
      {canAccessVaperite && group('vaperite', 'VAPERITE', <>
        {canSeePath("/vaperite/staging") && (
          <NavItem icon={<NavGlyph name="vaperiteItemStaging" />} label="Vaperite Item Staging" to="/vaperite/staging" selected={isActive("/vaperite/staging")} />
        )}
        {canSeePath("/vaperite/products") && (
          <NavItem icon={<NavGlyph name="vaperiteProducts" />} label="Vaperite Products" to="/vaperite/products" selected={isActive("/vaperite/products")} />
        )}
        {canSeePath("/vaperite/stores") && (
          <NavItem icon={<NavGlyph name="vaperiteStores" />} label="Vaperite Stores" to="/vaperite/stores" selected={isActive("/vaperite/stores")} />
        )}
        {canSeePath("/vaperite/order-tablets") && (
          <NavItem icon={<NavGlyph name="orderManagerTablets" />} label="Vaperite Order Tablets" to="/vaperite/order-tablets" selected={isActive("/vaperite/order-tablets")} />
        )}
        {canSeePath("/vaperite/sync") && (
          <NavItem icon={<NavGlyph name="syncOverview" />} label="Vaperite Sync" to="/vaperite/sync" selected={isActive("/vaperite/sync")} />
        )}
      </>)}

      {/* ======== MARKETING ======== */}
      {/* Jason, 26 Oct 2026: the Discount Engine's four pages lead Marketing (their page keys stay
          discounts.*, so staff page grants are unchanged) and Store Messages closes it. Cart Level
          Promotions and Rewards Program ran on Voucherify and were removed. */}
      {(canView('banners') || hasAnyRole(['owner', 'admin', 'staff'])) && group('marketing', 'MARKETING', <>
        {hasAnyRole(['owner', 'admin', 'staff']) && canSeePath("/discounts/promotions") && (
          <NavItem icon={<NavGlyph name="promotions" />} label="Promotions" to="/discounts/promotions" selected={location.pathname.startsWith("/discounts/promotions")} />
        )}
        {hasAnyRole(['owner', 'admin', 'staff']) && canSeePath("/discounts/customers") && (
          <NavItem icon={<NavGlyph name="customers" />} label="Customers" to="/discounts/customers" selected={location.pathname.startsWith("/discounts/customers")} />
        )}
        {hasAnyRole(['owner', 'admin', 'staff']) && canSeePath("/discounts/loyalty") && (
          <NavItem icon={<NavGlyph name="loyalty" />} label="Loyalty" to="/discounts/loyalty" selected={isActive("/discounts/loyalty")} />
        )}
        {hasAnyRole(['owner', 'admin', 'staff']) && canSeePath("/discounts/referrals") && (
          <NavItem icon={<NavGlyph name="referrals" />} label="Referrals" to="/discounts/referrals" selected={isActive("/discounts/referrals")} />
        )}
        {roleAdmits && canSeePath("/messages") && (
            <NavItem icon={<NavGlyph name="storeMessages" />} label="Store Messages" to="/messages" selected={isActive("/messages")} />
        )}
      </>, canSeeSection('marketing') || canSeeSection('discounts'))}

      {/* ======== ADMIN TOOLS ======== */}
      {/* Absorbed the former TOOLS section — it held only Inventory Tools and
          Mission Builder, which is a header's worth of chrome for two rows that
          read as admin tooling anyway. Customer Map (lifted off the Customers
          page) sits with them. */}
      {roleAdmits && group('adminTools', 'ADMIN TOOLS', <>
        {canSeePath("/admin/staff") && (
          <NavItem icon={<NavGlyph name="staffUsers" />} label="Staff Users" to="/admin/staff" selected={isActive("/admin/staff")} />
        )}
        {canSeePath("/tools") && (
          <NavItem icon={<NavGlyph name="inventoryTools" />} label="Inventory Tools" to="/tools" selected={isActive("/tools")} />
        )}
        {canSeePath("/missions") && (
          <NavItem icon={<NavGlyph name="missionBuilder" />} label="Mission Builder" to="/missions" selected={isActive("/missions")} />
        )}
        {canSeePath("/customer-map") && (
          <NavItem icon={<NavGlyph name="customerMap" />} label="Customer Map" to="/customer-map" selected={isActive("/customer-map")} />
        )}
        {canSeePath("/admin/metrics") && (
          <NavItem icon={<NavGlyph name="systemParameters" />} label="System Parameters" to="/admin/metrics" selected={isActive("/admin/metrics")} />
        )}
        {canSeePath("/settings/system-core") && (
          <NavItem icon={<NavGlyph name="systemCore" />} label="System Core" to="/settings/system-core" selected={isActive("/settings/system-core")} />
        )}
        {resources["webhook-dispatches"] && canSeePath("/webhook-dispatches") && (
            <NavItem icon={<NavGlyph name="webhookDispatches" />} label="Webhook Dispatches" to="/webhook-dispatches" selected={isActive("/webhook-dispatches")} />
        )}
        {canSeePath("/admin-functions/sync-control") && (
          <NavItem icon={<NavGlyph name="syncControl" />} label="Sync Control" to="/admin-functions/sync-control" selected={isActive("/admin-functions/sync-control")} />
        )}
        {/* Hardwired to the SKC gateway's menu-sync shape; Vaperite's webhooks
            are inbound, so it cannot be pointed at them as-is. On the rail
            because the prototype has it, pending the pass with William. */}
        {canSeePath("/integrations/webhook-testing") && (
          <NavItem icon={<NavGlyph name="webhookTesting" />} label="Webhook Testing" to="/integrations/webhook-testing" selected={isActive("/integrations/webhook-testing")} />
        )}
        {/* A store-wide trading switch, so it rides on System Core access. */}
        {canSeePath("/settings/system-core") && (
          <NavItem
            icon={applyingHoliday ? <CircularProgress size={17} /> : <NavGlyph name="publicHolidayMode" />}
            label="Public Holiday Mode"
            onClick={applyPublicHolidayMode}
            selected={false}
          />
        )}
        {/* Public Holiday Manager lives on the Store Command Center now (the
            calendar button in its header) — Jason, 2026-09-14. Holidays are a
            per-store trading matter, which is that page's whole subject. */}
        {/* The page itself refuses to do anything outside local development
            (and so does the API); the row stays so the rail is the same
            everywhere. */}
        {canSeePath("/admin-functions/dev-menu") && (
          <NavItem icon={<NavGlyph name="devMenu" />} label="Dev Menu" to="/admin-functions/dev-menu" selected={isActive("/admin-functions/dev-menu")} />
        )}
      </>)}
    </Menu>
    </>
  );
};

export default CustomMenu;
